/**
 * Shared teaser math for the free public funnel.
 *
 * Two entry ramps read the same `Scan` rows from opposite directions:
 * `/free-backlink-checker` starts with a competitor, `/who-links-to-my-website`
 * starts with the visitor's own site. The framing differs, the arithmetic must
 * not: a "gap" that means one thing on one page and something subtly different
 * on the other is a reporting bug that no test would catch. So the numbers live
 * here once and both routes import them.
 *
 * Everything in this module is pure and client-safe: it derives from a snapshot
 * a loader already fetched, and touches neither the database nor the provider.
 */
// Type-only, and it must stay that way. This module is imported by client
// components, so pulling any VALUE out of a `.server.ts` file here would drag
// server code into the browser bundle.
import { type ScanResultSnapshot } from './scan.server.ts'

export type TeaserRow = {
	domain: string
	authority: number
	backlinks: number
	dofollow: boolean
	isNew: boolean
}

export type Teaser = {
	target: string
	totalReferringDomains: number
	rows: TeaserRow[]
	highAuthority: number
	totalBacklinks: number
	dofollowPct: number | null
	newLast30d: number
	nodeCount: number
}

/**
 * Referring domains present on `source` but absent from `comparedTo`.
 *
 * Deliberately direction-agnostic. On the competitor-first page this is "links
 * they have that you don't" (your opportunity); on the own-domain page the same
 * function also runs reversed to show "links you have that they don't" (your
 * edge). `subject` names whichever domain the rows were taken FROM, so the
 * caller never has to remember which way round it ran.
 */
export type Gap = {
	subject: string
	comparedTo: string
	count: number
	top: TeaserRow[]
}

/** The free tier shows this many referring domains; the rest ship in the report. */
export const TEASER_ROW_LIMIT = 25

/** A referring domain is "new" if first seen inside this window. */
export const NEW_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

function num(value: unknown): number | null {
	return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function itemsFromScan(scan: {
	resultSnapshot: unknown
}): Array<Record<string, unknown>> {
	const snapshot = scan.resultSnapshot as ScanResultSnapshot | null
	return (Array.isArray(snapshot?.items) ? snapshot.items : []) as Array<
		Record<string, unknown>
	>
}

export function rowsFromScan(scan: { resultSnapshot: unknown }): TeaserRow[] {
	const newCutoff = Date.now() - NEW_WINDOW_MS
	// `rank` is a 0-1000 domain-rank metric; normalize to a familiar 0-100
	// "Authority" score. (Not Ahrefs DR, so we don't label it "DR".)
	return itemsFromScan(scan)
		.map((item) => {
			const refPages = num(item.referring_pages) ?? 0
			const nofollowPages = num(item.referring_pages_nofollow) ?? 0
			const firstSeen =
				typeof item.first_seen === 'string' ? Date.parse(item.first_seen) : NaN
			return {
				domain: typeof item.domain === 'string' ? item.domain : '',
				authority: Math.round((num(item.rank) ?? 0) / 10),
				backlinks: num(item.backlinks) ?? 0,
				dofollow: refPages - nofollowPages > 0,
				isNew: !Number.isNaN(firstSeen) && firstSeen >= newCutoff,
			}
		})
		.filter((row) => row.domain)
		.sort((a, b) => b.authority - a.authority)
}

export function buildTeaser(scan: {
	targetDomain: string
	referringDomainsFound: number
	resultSnapshot: unknown
}): Teaser {
	const items = itemsFromScan(scan)
	const allRows = rowsFromScan(scan)
	const newCutoff = Date.now() - NEW_WINDOW_MS

	let totalBacklinks = 0
	let referringPages = 0
	let nofollowPages = 0
	let newLast30d = 0
	for (const item of items) {
		totalBacklinks += num(item.backlinks) ?? 0
		referringPages += num(item.referring_pages) ?? 0
		nofollowPages += num(item.referring_pages_nofollow) ?? 0
		const firstSeen =
			typeof item.first_seen === 'string' ? Date.parse(item.first_seen) : NaN
		if (!Number.isNaN(firstSeen) && firstSeen >= newCutoff) newLast30d++
	}
	const dofollowPct =
		referringPages > 0
			? Math.round(((referringPages - nofollowPages) / referringPages) * 100)
			: null

	return {
		target: scan.targetDomain,
		totalReferringDomains: scan.referringDomainsFound,
		rows: allRows.slice(0, TEASER_ROW_LIMIT),
		highAuthority: allRows.filter((row) => row.authority >= 70).length,
		totalBacklinks,
		dofollowPct,
		newLast30d,
		// Graph density reflects real scale. The cap is COMPRESSION: 12,000
		// domains drawn as 42 nodes is still a picture of scale. A floor would be
		// INVENTION, which is a different thing: it drew nodes and travelling
		// pulses for links we did not find, and the "illustrative" caption under
		// the canvas does not cover a picture that contradicts the number beside
		// it. So 0 stays 0 (the header hides the canvas entirely) and 1-9 draw
		// exactly what they are.
		nodeCount: Math.min(scan.referringDomainsFound, 42),
	}
}

/**
 * The parent of a subdomain, for a copy-only "did you mean" hint.
 *
 * The common way to get an empty profile is scanning a host that was never
 * meant to attract links (`api.example.com`, `mail.example.com`) when the
 * interesting site is one level up. A dumb label split is enough for a hint:
 * it is offered as text, never auto-submitted, because submitting for them
 * would be a second billable provider pull they did not ask for.
 *
 * It is deliberately NOT a public-suffix lookup. `blog.co.uk` suggesting
 * `co.uk` is the known footgun, and it is rare enough to live with while the
 * hint stays copy: a wrong suggestion the visitor ignores costs nothing, but
 * shipping a suffix list to make a sentence marginally better does not pay.
 */
export function parentDomainHint(domain: string): string | null {
	const labels = domain.split('.').filter(Boolean)
	if (labels.length <= 2) return null
	return labels.slice(1).join('.')
}

export function domainSetFromScan(scan: {
	resultSnapshot: unknown
}): Set<string> {
	const set = new Set<string>()
	for (const item of itemsFromScan(scan)) {
		if (typeof item.domain === 'string') set.add(item.domain.toLowerCase())
	}
	return set
}

/**
 * The referring domains linking to `subject` but not to `comparedTo`.
 *
 * Call it with (competitor, you) for your opportunity, or (you, competitor) for
 * the links you already hold that they don't.
 */
export function buildGap(
	subjectScan: { targetDomain: string; resultSnapshot: unknown },
	comparedToScan: { resultSnapshot: unknown },
	comparedToDomain: string,
	limit = 6,
): Gap {
	const held = domainSetFromScan(comparedToScan)
	const missing = rowsFromScan(subjectScan).filter(
		(row) => !held.has(row.domain.toLowerCase()),
	)
	return {
		subject: subjectScan.targetDomain,
		comparedTo: comparedToDomain,
		count: missing.length,
		top: missing.slice(0, limit),
	}
}
